import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Download, ShieldCheck, Trash2 } from 'lucide-react';
import { recordingsAPI } from '../../../api/client';
import { apiError } from '../../../lib/host';
import { prettyNumber } from '../CallDialog';
import { ErrorBanner, PageHeading, useBasic } from '../ui';
import { rangeParams } from '../calls/callModel';
import Pager from '../calls/components/Pager';
import RecordingDetails from './components/RecordingDetails';
import RecordingFilters from './components/RecordingFilters';
import RecordingStats from './components/RecordingStats';
import RecordingsTable from './components/RecordingsTable';
import StepUpModal from './components/StepUpModal';
import { EMPTY_FILTERS } from './recordingsModel';
import '../calls/Calls.css';
import './Recordings.css';

const RETENTION = [[30, '30 days'], [90, '90 days'], [180, '180 days'], [365, '1 year'], [730, '2 years'], ['', 'Until deleted']];

function saveFile(res, fallback) {
  const name = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '')?.[1] || fallback;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Call Recordings: figures, filters (search, type, date, duration, team member, tag), the recordings (paged on the
 * server) and the selected one on the right with its player. Audio always comes through the API with the user's sign-in.
 * ?id=<recording> opens one (the voicemail email links here).
 */
export default function RecordingsPage() {
  const navigate = useNavigate();
  const { notify } = useBasic();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeId, setActiveId] = useState(() => Number(params.get('id')) || null);
  const [selected, setSelected] = useState(new Set());
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');
  const [stepUp, setStepUp] = useState(null); // {action, retry}: waiting for "confirm it's you"

  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); setSelected(new Set()); }, [query, filters, pageSize]);
  useEffect(() => { recordingsAPI.settings().then((res) => setSettings(res.data)).catch(() => {}); }, []);

  const latest = useRef(0);
  const load = useCallback(() => {
    const n = ++latest.current;
    recordingsAPI.list({
      ...(query && { q: query }), ...(filters.direction && { direction: filters.direction }), ...(filters.duration && { duration: filters.duration }),
      ...(filters.agent && { agent: filters.agent }), ...(filters.tag && { tag: filters.tag }), ...rangeParams(filters.range, filters.from, filters.to),
      page, per_page: pageSize,
    }).then((res) => { if (n === latest.current) { setData(res.data); setError(''); } })
      .catch((err) => n === latest.current && setError(apiError(err, 'Could not load recordings')));
  }, [query, filters, page, pageSize]);
  useEffect(load, [load]);

  const items = data?.items || [];
  const can = data?.can || {};
  const shownId = activeId ?? items[0]?.id ?? null;
  const filtered = query || Object.values(filters).some(Boolean);

  // Downloads and deletions may first need "confirm it's you" (step-up); after it, the action runs again.
  const needsStepUp = (err) => err.response?.data?.detail?.code === 'step_up_required';
  const download = async (r) => {
    try {
      saveFile(await recordingsAPI.audio(r.id, 'download'), `recording-${r.call_id}.mp3`);
      notify('Recording downloaded.');
    } catch (err) {
      if (needsStepUp(err)) setStepUp({ action: 'download', retry: () => download(r) });
      else notify(apiError(err, 'Could not download the recording'));
    }
  };
  const remove = async (list, confirmed = false) => {
    const one = list.length === 1;
    const who = one ? (list[0].contact_name || prettyNumber(list[0].number)) : `${list.length} recordings`;
    if (!confirmed && !window.confirm(`Delete the recording${one ? ` of the call with ${who}` : `s (${who})`}? The audio is removed for good; the call stays in Calls & Conversations.`)) return;
    let done = 0;
    try {
      for (const r of list) { await recordingsAPI.remove(r.id); done += 1; } // one at a time: each is checked and audited
      notify(one ? 'Recording deleted.' : `${list.length} recordings deleted.`);
      if (list.some((r) => r.id === shownId)) setActiveId(null);
      setSelected(new Set());
      load();
    } catch (err) {
      if (needsStepUp(err)) setStepUp({ action: 'delete', retry: () => remove(list.slice(done), true) });
      else { notify(apiError(err, 'Could not delete')); load(); }
    }
  };
  const onAction = (key, r) => {
    if (key === 'play' || key === 'view') setActiveId(r.id);
    else if (key === 'download') download(r);
    else if (key === 'call') navigate(`/calls/${r.call_id}`);
    else if (key === 'copy') navigator.clipboard?.writeText(r.number).then(() => notify(`${prettyNumber(r.number)} copied.`)).catch(() => {});
    else if (key === 'delete') remove([r]);
  };
  const pick = (ids, on) => setSelected((s) => { const n = new Set(s); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; });
  const saveRetention = async (value) => {
    const days = value === '' ? null : Number(value);
    if (!window.confirm(days ? `Keep recordings for ${days} days? Older ones are deleted automatically, including ones you have now.` : 'Keep recordings until someone deletes them?')) return;
    try {
      await recordingsAPI.saveSettings({ retention_days: days });
      setSettings({ ...settings, retention_days: days });
      notify('Retention saved.');
      load();
    } catch (err) { notify(apiError(err, 'Could not save retention')); }
  };

  return (
    <section className="page-view cl-page rc-page">
      <PageHeading title="Call Recordings" subtitle="Listen to, download and manage your call recordings." />
      <ErrorBanner message={error} />

      <RecordingStats stats={data?.stats} onFilter={(f) => setFilters({ ...EMPTY_FILTERS, ...f })} />
      <RecordingFilters search={search} onSearch={setSearch} filters={filters} onChange={setFilters}
        onReset={() => { setSearch(''); setQuery(''); setFilters(EMPTY_FILTERS); }} agents={data?.agents} tags={data?.tags} />

      <div className="cl-layout">
        <section className="panel cl-list">
          <div className="cl-list-head">
            <div><h2>Recordings ({data ? data.total : '…'})</h2><small>{data === null ? 'Loading…' : filtered ? 'Matching your filters' : 'Newest first'}</small></div>
            {selected.size > 0 && (
              <div className="rc-bulk"><span>{selected.size} selected</span>
                {can.delete && <button type="button" className="secondary-action danger" onClick={() => remove(items.filter((r) => selected.has(r.id)))}><Trash2 size={13} />Delete</button>}
                <button type="button" className="secondary-action" onClick={() => setSelected(new Set())}>Clear</button>
              </div>
            )}
          </div>
          {data === null && !error && <div className="empty-state cl-empty">Loading recordings…</div>}
          {items.length > 0 && (
            <RecordingsTable items={items} offset={(page - 1) * pageSize} activeId={shownId} selected={selected} onSelect={pick}
              onView={(r) => setActiveId(r.id)} onAction={onAction} can={can} />
          )}
          {data && items.length === 0 && (
            <div className="empty-state cl-empty">{filtered ? 'No recordings match your filters.' : 'No recordings yet. Turn on Record calls in Phone Numbers › Standard routing (or Settings › Calling) and new calls appear here.'}</div>
          )}
          {data?.total > 0 && <Pager page={page} pageSize={pageSize} total={data.total} onPage={setPage} onPageSize={setPageSize} noun="recording" />}
        </section>

        <div className="cl-side">
          {shownId && (
            <RecordingDetails key={shownId} recordingId={shownId} notify={notify} onChanged={load} onDownload={download}
              onDelete={(r) => remove([r])} onClose={() => { setActiveId(null); setParams({}, { replace: true }); }} />
          )}
          <section className="panel cl-card rc-retention">
            <h2><ShieldCheck size={15} /> Privacy &amp; retention</h2>
            <p className="cell-muted">Recordings are stored privately and only play for signed-in team members. Downloading or deleting asks you to confirm it's you with a code. Plays, downloads, deletions and refused attempts are logged.</p>
            {settings && (settings.can_edit
              ? <label className="cl-field">Keep recordings for
                  <select value={settings.retention_days ?? ''} onChange={(e) => saveRetention(e.target.value)}>
                    {RETENTION.some(([d]) => d === settings.retention_days) ? null : <option value={settings.retention_days}>{settings.retention_days} days</option>}
                    {RETENTION.map(([d, label]) => <option key={label} value={d}>{label}</option>)}
                  </select></label>
              : <p className="cell-muted">Kept for: <b>{settings.retention_days ? `${settings.retention_days} days` : 'until deleted'}</b></p>)}
            {can.download === false && <p className="cell-muted"><Download size={12} /> Downloading needs the Member role or higher.</p>}
          </section>
        </div>
      </div>
      {stepUp && <StepUpModal action={stepUp.action} onClose={() => setStepUp(null)} onVerified={() => { const { retry } = stepUp; setStepUp(null); retry(); }} />}
    </section>
  );
}
